import React, { useState } from 'react';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Modal, ModalSize } from '../../../components/modal/Modal';
import { createStyles } from 'utils/createStyle';
import { colors } from 'styles/settings';
import { useHistory } from 'react-router-dom';
import { Routes } from 'routes';
import { ProductType } from 'common/api/e-comm/models/Product';
import { useDispatch } from 'react-redux';
import { deleteCardThunk } from 'common/features/store/duck/wallet/duck';
import { useRootSelector } from 'rootStore';

interface ArchiveCardModalProps {
    isOpen: boolean;
    closeModal: () => void;
    cardId: string;
    cardType: ProductType;
}

type StringMap = {
    [key in ProductType]: {
        successText: [string, string, string];
        promptText: [string, string, string];
        verb: string;
        capitalVerb: string;
    };
};

const stringMap: StringMap = {
    [ProductType.EGiftCard]: {
        successText: [
            'This card has been Archived',
            'You have successfully removed this card. You can access this card in the Archive section.',
            'Close',
        ],
        promptText: [
            'Archive Card',
            'This will remove the card from the My Cards section. You can still access this card from the Archive section.',
            'Cancel',
        ],
        verb: 'archiving',
        capitalVerb: 'Archiving',
    },
    [ProductType.GiftCard]: {
        successText: [
            'This Card has been Removed',
            'You have successfully deleted this card from your wallet.',
            'Close',
        ],
        promptText: [
            'Remove This Card',
            'This will delete the card. You will not be able to access this card again unless you Add Card back to your wallet.',
            'Cancel',
        ],
        verb: 'removing',
        capitalVerb: 'Removing',
    },
    [ProductType.Reload]: {
        successText: ['', '', ''],
        promptText: ['', '', ''],
        verb: '',
        capitalVerb: '',
    },
};

const styles = createStyles({
    confirmButton: [
        'ml-auto mr-auto w-2/3 rounded-full bg-brand text-white font-semibold text-lg py-2 mb-2',
        { ':disabled': { backgroundColor: colors.greyAccent, color: colors.grey4 } },
    ],
});

function generateModalMetaData(
    submitSuccess: boolean,
    cardType: ProductType
): [string, string, string] {
    if (submitSuccess) {
        return stringMap[cardType].successText;
    }

    return stringMap[cardType].promptText;
}

const ArchiveCardModal = ({ isOpen, closeModal, cardId, cardType }: ArchiveCardModalProps) => {
    const [requestError, setRequestError] = useState('');
    const [loading, setLoading] = useState(false);
    const [archiveSuccess, setArchiveSuccess] = useState(false);
    const [title, caption, buttonText] = generateModalMetaData(archiveSuccess, cardType);
    const history = useHistory();
    const dispatch = useDispatch<any>();
    const userId = useRootSelector((r) => r.store.account.userId) as string;

    const onConfirmArchive = async (): Promise<void> => {
        setLoading(true);

        const res = await deleteCardThunk(userId, cardId)(dispatch);

        setLoading(false);

        if (res.error) {
            return setRequestError(
                `There was an issue ${stringMap[cardType].verb} the card. Please try again later.`
            );
        }

        setArchiveSuccess(true);
    };

    const onCloseModal = () => {
        closeModal();
        archiveSuccess && history.push(Routes.WalletMyCards);
    };

    return (
        <Modal
            title={title}
            isOpen={isOpen}
            onClose={onCloseModal}
            titleStyles={archiveSuccess ? 'text-success font-semibold' : 'text-error font-semibold'}
            size={[ModalSize.CONTENT_WIDTH, ModalSize.CONTENT_WIDTH]}
        >
            {requestError && (
                <label className="flex items-center justify-center text-error text-sm mb-4">
                    <FontAwesomeIcon icon={faTimesCircle} size="lg" className="mr-4" />
                    {requestError}
                </label>
            )}
            <h5 className="max-w-md font-light mb-10">{caption}</h5>
            {!archiveSuccess && (
                <button
                    onClick={onConfirmArchive}
                    disabled={loading || Boolean(requestError.length)}
                    className={styles.confirmButton}
                >
                    {loading ? `${stringMap[cardType].capitalVerb} Card...` : 'Yes'}
                </button>
            )}
            <button
                onClick={onCloseModal}
                className="text-brand font-semibold underline text-lg py-2"
            >
                {buttonText}
            </button>
        </Modal>
    );
};

export default ArchiveCardModal;
